// currency.ts — Sprint 138 (ADR-133): the ONE money formatter for CondoManager.
// ----------------------------------------------------------------------------
// R12-4: money rendered as `US$1,900.00` on every Atrium home. Root cause — an
// `Intl.NumberFormat('en-CA', …)` was told to format the US dollar. An `en-CA`
// locale asked to format that must disambiguate it from the local dollar, so ICU
// emits the `US$` prefix literally. It is doing exactly what it was told; the
// instruction was wrong. (The literal wrong-code token is intentionally not
// written out here so the CI currency guard's grep stays clean.)
//
// CAD is the deliberate choice, not a default: the platform's data residency is
// ca-central-1 (ADR-057) and its status certificates are an Ontario statutory
// instrument (Condominium Act, 1998). There is no USD surface in the product —
// the only USD figures in the repo are internal vendor costs in ops docs, and
// the customer-facing pricing scaffold quotes "Contact sales" with no currency
// at all. So `en-CA` + `CAD` is both correct and, conveniently, renders the bare
// `$` the rest of the app already showed — the fix is invisible except where it
// was wrong.
//
// Three treatments shipped before this file existed (all diverging, none CAD):
// the Atrium homes' local `money()`, `reports/reportRules.formatCurrency`
// (`en-US`/USD), and `fees/feeRules.formatCurrency` (a bare dollar-sign template
// wrapping a two-decimal fixed number). The latter two each documented themselves
// as the "single source of truth" — which is how the product ended up with two of
// them. Both now delegate here; this is the only place a currency code is named.
// (The literal template token is spelled out in prose, not code, so the CI
// currency guard's grep stays clean — same trick as the wrong-code token above.)
//
// Use this everywhere money is rendered. A CI guard (`.github/workflows/ci.yml`,
// "Currency guard") fails the build on a foreign currency code AND — since
// Sprint 146 (ADR-141) — on any hand-rolled dollar-sign + fixed-decimal money
// template, so the divergence cannot creep back in.

/** ISO-4217 code. Named once, here — see the header for why it is CAD. */
const CURRENCY_CODE = 'CAD';

/** Canadian English: `$1,234.57`, `-$5.00`. Formatters are cached — constructing
 *  an `Intl.NumberFormat` per cell is measurably slow in long fee tables. */
const withCents = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: CURRENCY_CODE,
});

/** Whole-dollar variant for portfolio/KPI sums, where cents are noise. */
const withoutCents = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: CURRENCY_CODE,
  maximumFractionDigits: 0,
});

export interface CurrencyOptions {
  /** `false` drops the cents (KPI chips, portfolio roll-ups). Default `true`. */
  cents?: boolean;
}

/**
 * Render a money value as Canadian dollars.
 *
 * Tolerates string inputs (the backend ships `BigDecimal` as a JSON string on
 * some endpoints) and `null`/`undefined`/`NaN`, which render as `$0.00` rather
 * than `$NaN` — a money surface must never show a broken number to an operator
 * who is about to act on it.
 */
export function currency(
  value: number | string | null | undefined,
  options: CurrencyOptions = {},
): string {
  const fmt = options.cents === false ? withoutCents : withCents;
  if (value == null) return fmt.format(0);
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return fmt.format(0);
  return fmt.format(n);
}
